/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Papa from 'papaparse';
import type { TimeSeriesMetersMap } from '@/types/timeSeriesConfig';
import type { DeviceSensorConfig } from '@/types/deviceSensor';
import type { SensorCsvRow } from '@/types/sensorCsv';

/* Import libraries */
import { getDeviceSensorCsvMetadataGroup } from '@/lib/sensors/csv/csvToDeviceSensorMetadata';
import { getMuensterBaseSensorTypes } from '@/lib/sensors/muenster/muensterBaseSensorTypes';
import { getSensorCsvSelection } from '@/lib/sensors/csv/sensorCsv';

function isType1Subsensor(value: string): boolean {
    return /^[A-Za-z]{1,2}_[0-9]{8}$/.test(value);
}

function getExtraSubsensorTemplates(
    anomaly: string,
    sensorTypes: DeviceSensorConfig['sensorTypes'],
): string[] {
    if (anomaly === 'bl_night' || anomaly === 'bl') {
        return sensorTypes.BL.map((sensorType) => `BL.${sensorType}`);
    }

    if (anomaly === 'heating_energy') {
        return sensorTypes.HE.map((sensorType) => `HE.${sensorType}`);
    }

    if (anomaly === 'pv') {
        return sensorTypes.PV.map((sensorType) => `P.PV.${sensorType}`);
    }

    return [];
}

function appendUniqueSubsensor(subsensors: string[], value: string): void {
    if (value && !subsensors.includes(value)) {
        subsensors.push(value);
    }
}

export function csvToTimeSeriesMeters(
    csv: string,
    deviceSensorConfig: Pick<DeviceSensorConfig, 'baseSensorTypes' | 'sensorTypes'>,
): TimeSeriesMetersMap {
    const parsedCsv = Papa.parse<SensorCsvRow>(csv, {
        delimiter: ';',
        header: true,
        skipEmptyLines: true,
    });

    return parsedCsv.data.reduce<TimeSeriesMetersMap>((accumulator, row) => {
        if (!row.Sensor) {
            return accumulator;
        }

        const sensorSelection = getSensorCsvSelection(row.Sensor);

        if (!sensorSelection) {
            return accumulator;
        }

        const { deviceName: meterKey, sensorType: subsensor } = sensorSelection;
        const metadataGroup = getDeviceSensorCsvMetadataGroup(row.Anomaly);
        const muensterBaseSensorTypes = row.Sensor.startsWith('Mue.') && metadataGroup
            ? getMuensterBaseSensorTypes([metadataGroup])
            : [];

        if (muensterBaseSensorTypes.length > 0) {
            accumulator[row.Sensor] ??= {
                subsensors: muensterBaseSensorTypes,
            };
        }

        if (!accumulator[meterKey]) {
            accumulator[meterKey] = {
                subsensors: [...deviceSensorConfig.baseSensorTypes],
            };
        }

        const meterSubsensors = accumulator[meterKey].subsensors;

        appendUniqueSubsensor(meterSubsensors, subsensor);

        if (row.Enabled !== '1' || !row.Anomaly) {
            return accumulator;
        }

        const extraSubsensorTemplates = getExtraSubsensorTemplates(
            row.Anomaly,
            deviceSensorConfig.sensorTypes,
        );

        if (extraSubsensorTemplates.length === 0) {
            return accumulator;
        }

        const extraSubsensorPrefix = isType1Subsensor(subsensor) ? subsensor : '';

        extraSubsensorTemplates.forEach((template) => {
            const nextSubsensor = extraSubsensorPrefix
                ? `${extraSubsensorPrefix}.${template}`
                : template;

            appendUniqueSubsensor(meterSubsensors, nextSubsensor);
        });

        return accumulator;
    }, {});
}
